import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import React, { useCallback, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import BookingDatesForm from '../../../forms/BookingDatesForm/BookingDatesForm';
import usePass from '../../../util/hooks/usePass';

const backButtonStyle = {
  display: { xs: 'flex', md: 'none' },
  position: 'absolute',
  top: 8,
  right: 8,
  zIndex: 2,
};
const MobileBookingModal = ({
  authorDisplayName,
  bookingLimit,
  currentListing,
  distanceToLocation,
  fetchLineItemsInProgress,
  fetchTimeSlotsInProgress,
  initialDates = null,
  isOwnListing,
  lineItems,
  minimumNights = 1,
  onClose,
  onFetchTimeSlots,
  onFetchTransactionLineItems,
  onSubmit,
  showAnnualPassError,
  showBookingDatesForm,
  timeSlots,
  title,
  voucher: appliedVoucher,
  voucherValidationError,
  voucherValidationInProgress,
}) => {
  const [passIsValid] = usePass();
  const [voucherCode, setVoucherCode] = useState(null);
  const lastBookingDataRef = useRef({ bookingDates: [null, null], guestCount: {} });

  const fetchLineItems = useCallback(({
    bookingDates,
    guestCount,
    voucher = null,
    extraLineItems = {},
    customLineItems = {},
  }) => {
    const [startDate, endDate] = bookingDates;
    const datesSelected = Boolean(startDate && endDate);

    if (datesSelected && onFetchTransactionLineItems) {
      onFetchTransactionLineItems({
        bookingData: {
          startDate: startDate.utc().toISOString(),
          endDate: endDate.utc().toISOString(),
          guestCount: {
            adultCount: guestCount.adult,
            childCount: guestCount.child,
            toddlerCount: guestCount.toddler,
            petCount: guestCount.pet,
          },
          ...(voucher ? { voucher } : {}),
          extraLineItems,
          customLineItems,
        },
        listingId: currentListing.id,
        isOwnListing,
      });
    }
  }, [onFetchTransactionLineItems, currentListing.id, isOwnListing]);

  const handleBookingDataChange = useCallback(({ bookingDates, guestCount, extraLineItems, customLineItems }) => {
    lastBookingDataRef.current = { bookingDates, guestCount, extraLineItems, customLineItems };

    const voucher = voucherCode ? { code: voucherCode } : null;
    fetchLineItems({ bookingDates, guestCount, voucher, extraLineItems, customLineItems });
  }, [fetchLineItems, voucherCode]);

  const handleApplyVoucher = useCallback((code) => {
    setVoucherCode(code);
    const { bookingDates, guestCount, extraLineItems, customLineItems } = lastBookingDataRef.current;
    const [startDate, endDate] = bookingDates;

    if (startDate && endDate) {
      fetchLineItems({ bookingDates, guestCount, voucher: { code }, extraLineItems, customLineItems });
    }
  }, [fetchLineItems]);

  return (
    <Box
      sx={{
        width: '100%',
        pb: '80px',
      }}
    >
      <IconButton onClick={onClose} sx={backButtonStyle} variant="roundMinimal">
        <CloseIcon />
      </IconButton>

      <Typography gutterBottom textAlign="center" variant="h2">
        {title}
      </Typography>
      <Typography mb={4} textAlign="center" variant="body2">
        <FormattedMessage id="BookingPanel.hostedBy" values={{ name: authorDisplayName }} />
      </Typography>

      <Box width="100%">
        {showBookingDatesForm ? (
          <BookingDatesForm
            appliedVoucher={appliedVoucher}
            bookingLimit={bookingLimit}
            currentListing={currentListing}
            distanceToLocation={distanceToLocation}
            fetchLineItemsInProgress={fetchLineItemsInProgress}
            fetchTimeSlotsInProgress={fetchTimeSlotsInProgress}
            initialDates={initialDates}
            lineItems={lineItems}
            minimumNights={minimumNights}
            onApplyVoucher={handleApplyVoucher}
            onBookingDataChange={handleBookingDataChange}
            onFetchTimeSlots={onFetchTimeSlots}
            onSubmit={onSubmit}
            showAnnualPassError={showAnnualPassError}
            timeSlots={timeSlots}
            voucherValidationError={voucherValidationError}
            voucherValidationInProgress={voucherValidationInProgress}
          />
        ) : null}
      </Box>
    </Box>
  );
};

export default MobileBookingModal;
